import { defineStore } from "pinia";
import { ref } from "vue";
import service from "@/services/hrm/timesheetService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useTimesheetStore = defineStore("timesheets", () => {
  const items = ref([]);
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);
  const listScope = ref("all");

  const filters = ref({
    search: "",
    project: "",
    status: "",
    employee_id: "",
    date_from: "",
    date_to: "",
    per_page: 25,
    page: 1,
  });

  function normalizePage(data, params) {
    if (data?.data && (data?.meta || data?.total !== undefined)) {
      items.value = data.data;
      pagination.value = {
        total: data.meta?.total ?? data.total ?? data.data.length,
        per_page: data.meta?.per_page ?? data.per_page ?? params.per_page ?? 25,
        current_page: data.meta?.current_page ?? data.current_page ?? 1,
      };
    } else {
      items.value = Array.isArray(data) ? data : [];
      pagination.value = {
        total: items.value.length,
        per_page: params.per_page ?? 25,
        current_page: 1,
      };
    }
  }

  async function fetchAll(extra = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_ATTENDANCE))) {
      items.value = [];
      pagination.value = { total: 0, per_page: 25, current_page: 1 };
      return;
    }

    listScope.value = "all";
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const { data } = await service.getAll(params);
      normalizePage(data, params);
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch timesheets";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchMine(extra = {}) {
    listScope.value = "mine";
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      const { data } = await service.getMine(params);
      normalizePage(data, params);
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function refreshList(extra = {}) {
    if (listScope.value === "mine") {
      return fetchMine(extra);
    }
    return fetchAll(extra);
  }

  async function create(payload) {
    loading.value = true;
    try {
      await service.create(payload);
      toast("Timesheet created");
      await refreshList({ page: 1 });
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to create", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await service.update(id, payload);
      toast("Timesheet updated");
      await refreshList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to update", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    try {
      await service.delete(id);
      toast("Timesheet deleted");
      await refreshList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to delete", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    try {
      await service.bulkDelete(ids);
      toast("Selected timesheets deleted");
      await refreshList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to bulk delete", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function approve(id, status, notes = "") {
    loading.value = true;
    try {
      await service.approve(id, { status, notes });
      toast(`Timesheet ${status.toLowerCase()}`);
      await refreshList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to approve", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkApprove(ids, status, notes = "") {
    loading.value = true;
    try {
      await service.bulkApprove(ids, status, notes);
      toast(`Marked ${ids.length} as ${status.toLowerCase()}`);
      await refreshList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to update", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function exportCsv(params = {}) {
    try {
      const res = await service.exportCsv({ ...filters.value, ...params });
      downloadBlob(res.data, "timesheets.csv");
    } catch (e) {
      toast("Export CSV failed", "error");
    }
  }

  async function exportPdf(params = {}) {
    try {
      const res = await service.exportPdf({ ...filters.value, ...params });
      downloadBlob(res.data, "timesheets.pdf");
    } catch (e) {
      toast("Export PDF failed", "error");
    }
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(new Blob([blob]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return {
    items,
    pagination,
    loading,
    error,
    filters,
    listScope,
    fetchAll,
    fetchMine,
    refreshList,
    create,
    update,
    remove,
    bulkDelete,
    approve,
    bulkApprove,
    exportCsv,
    exportPdf,
  };
});
